import { Stack, Title } from '@mantine/core';
import { InfoRow } from 'components/InfoRow';
import { i18nKeys, useTranslation } from 'locales';
import Amount from 'shared/components/Amount';
import Percentage from 'shared/components/Percentage';
import { CalculationType, InterestCalculationType } from 'shared/forms/PreferencesForm';
import type { Invoice } from 'types/store/invoice-state';

type LatePaymentInfoTermsModalProps = { invoice: Invoice };

export default function LatePaymentInfoTermsModal({ invoice }: LatePaymentInfoTermsModalProps) {
  const { t } = useTranslation();

  const {
    terms_and_conditions: { fix_fee, interest, penalty_clause },
    localized_money_object,
    currency,
  } = invoice;

  return (
    <Stack>
      {!!penalty_clause?.value && !!penalty_clause?.minimum && (
        <Stack gap={4}>
          <Title order={5}>{t(i18nKeys.PENALTY_CLAUSE)}</Title>
          <InfoRow label={t(i18nKeys.AMOUNT_MIN)}>
            <InfoRow.Value>
              <Amount
                localizedValue={penalty_clause.minimum}
                value={localized_money_object.terms_and_conditions.penalty_clause.minimum}
                suffix={currency}
              />
            </InfoRow.Value>
          </InfoRow>
          <InfoRow
            label={t(
              penalty_clause.type === InterestCalculationType.PERCENTAGE
                ? i18nKeys.VALUE
                : i18nKeys.AMOUNT,
            )}
          >
            <InfoRow.Value>
              {penalty_clause.type === InterestCalculationType.PERCENTAGE ? (
                <Percentage value={penalty_clause.value} />
              ) : (
                <Amount
                  localizedValue={penalty_clause.value}
                  value={localized_money_object.terms_and_conditions.penalty_clause.value}
                  suffix={currency}
                />
              )}
            </InfoRow.Value>
          </InfoRow>
        </Stack>
      )}
      {!!interest?.value && (
        <Stack gap={4}>
          <Title order={5}>{t(i18nKeys.FORM.PREFERENCES.INTEREST)}</Title>
          <InfoRow label={t(i18nKeys.FORM.PREFERENCES.PERIODICITY)}>
            {t(
              i18nKeys.CALCULATION_TYPE[
                // @ts-expect-error
                Object.keys(CalculationType).find(
                  key => interest.period === CalculationType[key as keyof typeof CalculationType],
                ) as CalculationType
              ],
            )}
          </InfoRow>
          <InfoRow label={t(i18nKeys.CLIENT.DETAIL.INTEREST_RATE)}>
            <InfoRow.Value>
              <Percentage value={interest.value} />
            </InfoRow.Value>
          </InfoRow>
        </Stack>
      )}
      {!!fix_fee?.value && (
        <Stack gap={4}>
          <Title order={5}>{t(i18nKeys.FORM.PREFERENCES.FIX_FEE)}</Title>
          <InfoRow label={t(i18nKeys.AMOUNT)}>
            <InfoRow.Value>
              <Amount
                localizedValue={fix_fee.value}
                value={localized_money_object.terms_and_conditions.fix_fee.value}
                suffix={currency}
              />
            </InfoRow.Value>
          </InfoRow>
        </Stack>
      )}
    </Stack>
  );
}
